import { createSlice } from "@reduxjs/toolkit";

// Plain types shared with components for their own local (useState +
// useSearchParams) filter/sort/tab state - see docs/new-module-checklist.md
// Section 5: this state itself does not live in Redux, only the fetched
// data below does.
export interface TableSort {
  keytoSort: string;
  direction: "asc" | "desc";
}

export type OptifundDashboardTab = "performance" | "brands" | "marketing-funnel" | "brand-stages";
export type PerformancePeriod = "daily" | "weekly" | "monthly";

export interface OptifundDashboardSlice {
  selectedWebsiteSharedUid: string | null;

  performanceData: any;
  brandsData: any;
  usersData: any;
  performanceLoading: boolean;
  brandsLoading: boolean;
  usersLoading: boolean;
  // Errors below are per-query (not per-tab) so each Brands table can show
  // its own "failed to load" state with its own retry button, instead of
  // one failure taking down the whole tab (see Brands.tsx).
  brandsError: boolean;
  brandsMonthlyData: any;
  brandsMonthlyLoading: boolean;
  brandsMonthlyError: boolean;
  brandsReportAttemptData: any;
  brandsReportAttemptLoading: boolean;
  brandsReportAttemptError: boolean;

  // Performance tab - each period is its own query with its own cached
  // result/loading flag (see OptifundDashboardQueryIds.performance*Id).
  // performanceData/performanceLoading above are the Daily period's data.
  performanceWeeklyData: any;
  performanceWeeklyLoading: boolean;
  performanceMonthlyData: any;
  performanceMonthlyLoading: boolean;

  // Users tab
  usersLeadToQualifiedData: any;
  usersLeadToQualifiedLoading: boolean;
  usersQualifiedByZipData: any;
  usersQualifiedByZipLoading: boolean;
  usersLeadToPaidData: any;
  usersLeadToPaidLoading: boolean;
  usersLeadToMatchedData: any;
  usersLeadToMatchedLoading: boolean;
  usersStep1AllData: any;
  usersStep1AllLoading: boolean;
  usersStep1LeadData: any;
  usersStep1LeadLoading: boolean;
  usersStep1QualifiedData: any;
  usersStep1QualifiedLoading: boolean;
  usersStep2AllData: any;
  usersStep2AllLoading: boolean;
  usersStep2LeadData: any;
  usersStep2LeadLoading: boolean;
  usersStep2QualifiedData: any;
  usersStep2QualifiedLoading: boolean;
  usersStep3AllData: any;
  usersStep3AllLoading: boolean;
  usersStep3LeadData: any;
  usersStep3LeadLoading: boolean;
  usersStep3QualifiedData: any;
  usersStep3QualifiedLoading: boolean;
  usersStep4AllData: any;
  usersStep4AllLoading: boolean;
  usersStep4LeadData: any;
  usersStep4LeadLoading: boolean;
  usersStep4QualifiedData: any;
  usersStep4QualifiedLoading: boolean;
  usersStep5LeadData: any;
  usersStep5LeadLoading: boolean;
  usersStep5QualifiedData: any;
  usersStep5QualifiedLoading: boolean;
  usersStep6AllData: any;
  usersStep6AllLoading: boolean;
  usersStep6LeadData: any;
  usersStep6LeadLoading: boolean;
  usersStep6QualifiedData: any;
  usersStep6QualifiedLoading: boolean;
  usersStep7AllData: any;
  usersStep7AllLoading: boolean;
  usersStep7LeadData: any;
  usersStep7LeadLoading: boolean;
  usersStep7QualifiedData: any;
  usersStep7QualifiedLoading: boolean;

  // Brand Stages tab - raw Lead Funnel rows for all brands (brand_uid '0'),
  // the brand list (derived from the Brand Status Query's Paid rows), one
  // Lead Funnel result per brand_uid, and the drill-down's Brand Status
  // Query results keyed by brand_uid|state|start|end.
  brandStagesAllFunnelData: any;
  brandStagesAllFunnelLoading: boolean;
  brandStagesAllFunnelError: boolean;
  brandStagesBrandsData: any;
  brandStagesBrandFunnelsData: Record<string, any>;
  brandStagesMatrixLoading: boolean;
  brandStagesMatrixError: boolean;
  brandStagesDrillCache: Record<string, any>;
}

const initialState: OptifundDashboardSlice = {
  selectedWebsiteSharedUid: null,

  performanceData: null,
  brandsData: null,
  usersData: null,
  performanceLoading: false,
  brandsLoading: false,
  usersLoading: false,
  brandsError: false,
  brandsMonthlyData: null,
  brandsMonthlyLoading: false,
  brandsMonthlyError: false,
  brandsReportAttemptData: null,
  brandsReportAttemptLoading: false,
  brandsReportAttemptError: false,

  performanceWeeklyData: null,
  performanceWeeklyLoading: false,
  performanceMonthlyData: null,
  performanceMonthlyLoading: false,

  usersLeadToQualifiedData: null,
  usersLeadToQualifiedLoading: false,
  usersQualifiedByZipData: null,
  usersQualifiedByZipLoading: false,
  usersLeadToPaidData: null,
  usersLeadToPaidLoading: false,
  usersLeadToMatchedData: null,
  usersLeadToMatchedLoading: false,
  usersStep1AllData: null,
  usersStep1AllLoading: false,
  usersStep1LeadData: null,
  usersStep1LeadLoading: false,
  usersStep1QualifiedData: null,
  usersStep1QualifiedLoading: false,
  usersStep2AllData: null,
  usersStep2AllLoading: false,
  usersStep2LeadData: null,
  usersStep2LeadLoading: false,
  usersStep2QualifiedData: null,
  usersStep2QualifiedLoading: false,
  usersStep3AllData: null,
  usersStep3AllLoading: false,
  usersStep3LeadData: null,
  usersStep3LeadLoading: false,
  usersStep3QualifiedData: null,
  usersStep3QualifiedLoading: false,
  usersStep4AllData: null,
  usersStep4AllLoading: false,
  usersStep4LeadData: null,
  usersStep4LeadLoading: false,
  usersStep4QualifiedData: null,
  usersStep4QualifiedLoading: false,
  usersStep5LeadData: null,
  usersStep5LeadLoading: false,
  usersStep5QualifiedData: null,
  usersStep5QualifiedLoading: false,
  usersStep6AllData: null,
  usersStep6AllLoading: false,
  usersStep6LeadData: null,
  usersStep6LeadLoading: false,
  usersStep6QualifiedData: null,
  usersStep6QualifiedLoading: false,
  usersStep7AllData: null,
  usersStep7AllLoading: false,
  usersStep7LeadData: null,
  usersStep7LeadLoading: false,
  usersStep7QualifiedData: null,
  usersStep7QualifiedLoading: false,

  brandStagesAllFunnelData: null,
  brandStagesAllFunnelLoading: false,
  brandStagesAllFunnelError: false,
  brandStagesBrandsData: null,
  brandStagesBrandFunnelsData: {},
  brandStagesMatrixLoading: false,
  brandStagesMatrixError: false,
  brandStagesDrillCache: {},
};

const optifundDashboardSlice = createSlice({
  name: "optifundDashboard",
  initialState,
  reducers: {
    setSelectedWebsiteSharedUid: (state, action) => {
      state.selectedWebsiteSharedUid = action.payload;
    },
    setPerformanceData: (state, action) => {
      state.performanceData = action.payload;
    },
    setBrandsData: (state, action) => {
      state.brandsData = action.payload;
    },
    setUsersData: (state, action) => {
      state.usersData = action.payload;
    },
    setPerformanceLoading: (state, action) => {
      state.performanceLoading = action.payload;
    },
    setBrandsLoading: (state, action) => {
      state.brandsLoading = action.payload;
    },
    setBrandsError: (state, action) => {
      state.brandsError = action.payload;
    },
    setUsersLoading: (state, action) => {
      state.usersLoading = action.payload;
    },
    setBrandsMonthlyData: (state, action) => {
      state.brandsMonthlyData = action.payload;
    },
    setBrandsMonthlyLoading: (state, action) => {
      state.brandsMonthlyLoading = action.payload;
    },
    setBrandsMonthlyError: (state, action) => {
      state.brandsMonthlyError = action.payload;
    },
    setBrandsReportAttemptData: (state, action) => {
      state.brandsReportAttemptData = action.payload;
    },
    setBrandsReportAttemptLoading: (state, action) => {
      state.brandsReportAttemptLoading = action.payload;
    },
    setBrandsReportAttemptError: (state, action) => {
      state.brandsReportAttemptError = action.payload;
    },
    setPerformanceWeeklyData: (state, action) => {
      state.performanceWeeklyData = action.payload;
    },
    setPerformanceWeeklyLoading: (state, action) => {
      state.performanceWeeklyLoading = action.payload;
    },
    setPerformanceMonthlyData: (state, action) => {
      state.performanceMonthlyData = action.payload;
    },
    setPerformanceMonthlyLoading: (state, action) => {
      state.performanceMonthlyLoading = action.payload;
    },
    setUsersLeadToQualifiedData: (state, action) => {
      state.usersLeadToQualifiedData = action.payload;
    },
    setUsersLeadToQualifiedLoading: (state, action) => {
      state.usersLeadToQualifiedLoading = action.payload;
    },
    setUsersQualifiedByZipData: (state, action) => {
      state.usersQualifiedByZipData = action.payload;
    },
    setUsersQualifiedByZipLoading: (state, action) => {
      state.usersQualifiedByZipLoading = action.payload;
    },
    setUsersLeadToPaidData: (state, action) => {
      state.usersLeadToPaidData = action.payload;
    },
    setUsersLeadToPaidLoading: (state, action) => {
      state.usersLeadToPaidLoading = action.payload;
    },
    setUsersLeadToMatchedData: (state, action) => {
      state.usersLeadToMatchedData = action.payload;
    },
    setUsersLeadToMatchedLoading: (state, action) => {
      state.usersLeadToMatchedLoading = action.payload;
    },
    setUsersStep1AllData: (state, action) => {
      state.usersStep1AllData = action.payload;
    },
    setUsersStep1AllLoading: (state, action) => {
      state.usersStep1AllLoading = action.payload;
    },
    setUsersStep1LeadData: (state, action) => {
      state.usersStep1LeadData = action.payload;
    },
    setUsersStep1LeadLoading: (state, action) => {
      state.usersStep1LeadLoading = action.payload;
    },
    setUsersStep1QualifiedData: (state, action) => {
      state.usersStep1QualifiedData = action.payload;
    },
    setUsersStep1QualifiedLoading: (state, action) => {
      state.usersStep1QualifiedLoading = action.payload;
    },
    setUsersStep2AllData: (state, action) => {
      state.usersStep2AllData = action.payload;
    },
    setUsersStep2AllLoading: (state, action) => {
      state.usersStep2AllLoading = action.payload;
    },
    setUsersStep2LeadData: (state, action) => {
      state.usersStep2LeadData = action.payload;
    },
    setUsersStep2LeadLoading: (state, action) => {
      state.usersStep2LeadLoading = action.payload;
    },
    setUsersStep2QualifiedData: (state, action) => {
      state.usersStep2QualifiedData = action.payload;
    },
    setUsersStep2QualifiedLoading: (state, action) => {
      state.usersStep2QualifiedLoading = action.payload;
    },
    setUsersStep3AllData: (state, action) => {
      state.usersStep3AllData = action.payload;
    },
    setUsersStep3AllLoading: (state, action) => {
      state.usersStep3AllLoading = action.payload;
    },
    setUsersStep3LeadData: (state, action) => {
      state.usersStep3LeadData = action.payload;
    },
    setUsersStep3LeadLoading: (state, action) => {
      state.usersStep3LeadLoading = action.payload;
    },
    setUsersStep3QualifiedData: (state, action) => {
      state.usersStep3QualifiedData = action.payload;
    },
    setUsersStep3QualifiedLoading: (state, action) => {
      state.usersStep3QualifiedLoading = action.payload;
    },
    setUsersStep4AllData: (state, action) => {
      state.usersStep4AllData = action.payload;
    },
    setUsersStep4AllLoading: (state, action) => {
      state.usersStep4AllLoading = action.payload;
    },
    setUsersStep4LeadData: (state, action) => {
      state.usersStep4LeadData = action.payload;
    },
    setUsersStep4LeadLoading: (state, action) => {
      state.usersStep4LeadLoading = action.payload;
    },
    setUsersStep4QualifiedData: (state, action) => {
      state.usersStep4QualifiedData = action.payload;
    },
    setUsersStep4QualifiedLoading: (state, action) => {
      state.usersStep4QualifiedLoading = action.payload;
    },
    setUsersStep5LeadData: (state, action) => {
      state.usersStep5LeadData = action.payload;
    },
    setUsersStep5LeadLoading: (state, action) => {
      state.usersStep5LeadLoading = action.payload;
    },
    setUsersStep5QualifiedData: (state, action) => {
      state.usersStep5QualifiedData = action.payload;
    },
    setUsersStep5QualifiedLoading: (state, action) => {
      state.usersStep5QualifiedLoading = action.payload;
    },
    setUsersStep6AllData: (state, action) => {
      state.usersStep6AllData = action.payload;
    },
    setUsersStep6AllLoading: (state, action) => {
      state.usersStep6AllLoading = action.payload;
    },
    setUsersStep6LeadData: (state, action) => {
      state.usersStep6LeadData = action.payload;
    },
    setUsersStep6LeadLoading: (state, action) => {
      state.usersStep6LeadLoading = action.payload;
    },
    setUsersStep6QualifiedData: (state, action) => {
      state.usersStep6QualifiedData = action.payload;
    },
    setUsersStep6QualifiedLoading: (state, action) => {
      state.usersStep6QualifiedLoading = action.payload;
    },
    setUsersStep7AllData: (state, action) => {
      state.usersStep7AllData = action.payload;
    },
    setUsersStep7AllLoading: (state, action) => {
      state.usersStep7AllLoading = action.payload;
    },
    setUsersStep7LeadData: (state, action) => {
      state.usersStep7LeadData = action.payload;
    },
    setUsersStep7LeadLoading: (state, action) => {
      state.usersStep7LeadLoading = action.payload;
    },
    setUsersStep7QualifiedData: (state, action) => {
      state.usersStep7QualifiedData = action.payload;
    },
    setUsersStep7QualifiedLoading: (state, action) => {
      state.usersStep7QualifiedLoading = action.payload;
    },
    setBrandStagesAllFunnelData: (state, action) => {
      state.brandStagesAllFunnelData = action.payload;
    },
    setBrandStagesAllFunnelLoading: (state, action) => {
      state.brandStagesAllFunnelLoading = action.payload;
    },
    setBrandStagesAllFunnelError: (state, action) => {
      state.brandStagesAllFunnelError = action.payload;
    },
    setBrandStagesBrandsData: (state, action) => {
      state.brandStagesBrandsData = action.payload;
    },
    setBrandStagesBrandFunnelsData: (state, action) => {
      state.brandStagesBrandFunnelsData = action.payload;
    },
    setBrandStagesMatrixLoading: (state, action) => {
      state.brandStagesMatrixLoading = action.payload;
    },
    setBrandStagesMatrixError: (state, action) => {
      state.brandStagesMatrixError = action.payload;
    },
    setBrandStagesDrillCacheEntry: (state, action) => {
      state.brandStagesDrillCache[action.payload.key] = action.payload.rows;
    },
    clearBrandStagesDrillCache: (state) => {
      state.brandStagesDrillCache = {};
    },
    clearOptifundDashboard: () => initialState,
  },
});

export const {
  setSelectedWebsiteSharedUid,
  setPerformanceData,
  setBrandsData,
  setUsersData,
  setPerformanceLoading,
  setBrandsLoading,
  setBrandsError,
  setUsersLoading,
  setBrandsMonthlyData,
  setBrandsMonthlyLoading,
  setBrandsMonthlyError,
  setBrandsReportAttemptData,
  setBrandsReportAttemptLoading,
  setBrandsReportAttemptError,
  setPerformanceWeeklyData,
  setPerformanceWeeklyLoading,
  setPerformanceMonthlyData,
  setPerformanceMonthlyLoading,
  setUsersLeadToQualifiedData,
  setUsersLeadToQualifiedLoading,
  setUsersQualifiedByZipData,
  setUsersQualifiedByZipLoading,
  setUsersLeadToPaidData,
  setUsersLeadToPaidLoading,
  setUsersLeadToMatchedData,
  setUsersLeadToMatchedLoading,
  setUsersStep1AllData,
  setUsersStep1AllLoading,
  setUsersStep1LeadData,
  setUsersStep1LeadLoading,
  setUsersStep1QualifiedData,
  setUsersStep1QualifiedLoading,
  setUsersStep2AllData,
  setUsersStep2AllLoading,
  setUsersStep2LeadData,
  setUsersStep2LeadLoading,
  setUsersStep2QualifiedData,
  setUsersStep2QualifiedLoading,
  setUsersStep3AllData,
  setUsersStep3AllLoading,
  setUsersStep3LeadData,
  setUsersStep3LeadLoading,
  setUsersStep3QualifiedData,
  setUsersStep3QualifiedLoading,
  setUsersStep4AllData,
  setUsersStep4AllLoading,
  setUsersStep4LeadData,
  setUsersStep4LeadLoading,
  setUsersStep4QualifiedData,
  setUsersStep4QualifiedLoading,
  setUsersStep5LeadData,
  setUsersStep5LeadLoading,
  setUsersStep5QualifiedData,
  setUsersStep5QualifiedLoading,
  setUsersStep6AllData,
  setUsersStep6AllLoading,
  setUsersStep6LeadData,
  setUsersStep6LeadLoading,
  setUsersStep6QualifiedData,
  setUsersStep6QualifiedLoading,
  setUsersStep7AllData,
  setUsersStep7AllLoading,
  setUsersStep7LeadData,
  setUsersStep7LeadLoading,
  setUsersStep7QualifiedData,
  setUsersStep7QualifiedLoading,
  setBrandStagesAllFunnelData,
  setBrandStagesAllFunnelLoading,
  setBrandStagesAllFunnelError,
  setBrandStagesBrandsData,
  setBrandStagesBrandFunnelsData,
  setBrandStagesMatrixLoading,
  setBrandStagesMatrixError,
  setBrandStagesDrillCacheEntry,
  clearBrandStagesDrillCache,
  clearOptifundDashboard,
} = optifundDashboardSlice.actions;

export default optifundDashboardSlice.reducer;
